/**
 * MIRROR von `src/lib/bv-modul-templates.ts` (Backend).
 *
 * Backend ist Source of Truth — Snapshot-Tests dort halten Drift gegen
 * busch-app fest. Diese Subkopie existiert nur, weil das Frontend keinen
 * direkten Cross-Boundary-Import auf Backend-`src/` haben soll (Build-
 * Boundary; Backend zieht u.a. drizzle, das nichts im Browser-Bundle zu
 * suchen hat).
 *
 * Drift-Schutz: `bv-lib-drift.test.ts` vergleicht diese Datei strukturell
 * gegen das Backend-Original und bricht CI bei Abweichung.
 *
 * Bei Backend-Änderungen 1:1 hierher übernehmen.
 *
 * BV-Plan §5.1.
 */

export const bvModulIdValues = [
  'haustechnik',
  'innenreinigung',
  'aussenreinigung',
  'gruenflaechenpflege',
  'winterdienst',
  'bereitschaftsdienst',
  'sonderarbeiten',
] as const;
export type BvModulId = (typeof bvModulIdValues)[number];

export const MODUL_ORDER = bvModulIdValues;

export const MODUL_LABELS: Record<BvModulId, string> = {
  haustechnik: 'Haustechnik',
  innenreinigung: 'Innenreinigung',
  aussenreinigung: 'Außenreinigung',
  gruenflaechenpflege: 'Grünflächenpflege',
  winterdienst: 'Winterdienst',
  bereitschaftsdienst: 'Bereitschaftsdienst',
  sonderarbeiten: 'Sonderarbeiten',
};

export const MODUL_SAISON: Partial<Record<BvModulId, string>> = {
  gruenflaechenpflege: '01.04.–31.10.',
  winterdienst: '01.11.–31.03.',
};

// ---- Frequenz-Anzeige (für Positions-Katalog) ----

export type FrequenzPeriod =
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'yearly'
  | 'asNeeded'
  | 'ongoing'
  | 'agreed';

export interface Frequenz {
  count: number;
  period: FrequenzPeriod;
}

const FREQUENZ_PERIOD_LABEL: Record<FrequenzPeriod, string> = {
  weekly: 'pro Woche',
  monthly: 'pro Monat',
  quarterly: 'pro Quartal',
  yearly: 'pro Jahr',
  asNeeded: 'nach Bedarf',
  ongoing: 'laufend',
  agreed: 'nach Vereinbarung',
};

export function formatFrequenz(f: Frequenz): string {
  if (f.period === 'asNeeded' || f.period === 'ongoing' || f.period === 'agreed') {
    return FREQUENZ_PERIOD_LABEL[f.period];
  }
  return `${f.count}× ${FREQUENZ_PERIOD_LABEL[f.period]}`;
}

// ---- Pricing-Konfig pro Modul ----

export interface PricingHaustechnik {
  klassePreise: { klein: number; mittel: number; gross: number };
}

export interface PricingInnenreinigung {
  klassePreise: { klein: number; mittel: number; gross: number };
  frequenzFaktor: Record<'0.5' | '1' | '2' | '3' | '4' | '5' | '6' | '7', number>;
}

export interface PricingProQm {
  euroProQm: number;
}

export interface PricingBereitschaft {
  klein: number;
  mittel: number;
  gross: number;
}

export type PricingNoneAvailable = Record<string, never>;

export type PricingPerModul = {
  haustechnik: PricingHaustechnik;
  innenreinigung: PricingInnenreinigung;
  aussenreinigung: PricingProQm;
  gruenflaechenpflege: PricingProQm;
  winterdienst: PricingProQm;
  bereitschaftsdienst: PricingBereitschaft;
  sonderarbeiten: PricingNoneAvailable;
};

// ---- Modul-Template ----

export interface ModulTemplate<M extends BvModulId = BvModulId> {
  id: M;
  label: string;
  reihenfolge: number;
  saison?: string;
  defaultBausteinBezeichnung: string;
  defaultKalkulationstyp:
    | 'pauschal'
    | 'mengen_x_preis_x_frequenz'
    | 'saisonal_verteilt'
    | 'bereitschaft_plus_einsatz';
  defaultFrequenz?: Frequenz;
  defaultBeschreibungIntro?: string;
  pricing: PricingPerModul[M];
}

export const MODUL_TEMPLATES: { [M in BvModulId]: ModulTemplate<M> } = {
  haustechnik: {
    id: 'haustechnik',
    label: 'Haustechnik',
    reihenfolge: 1,
    defaultBausteinBezeichnung: 'Haustechnik (Hausmeister-Pauschale)',
    defaultKalkulationstyp: 'pauschal',
    defaultFrequenz: { count: 1, period: 'weekly' },
    defaultBeschreibungIntro:
      'Hausmeister-Pauschale für die wöchentliche Betreuung der technischen und allgemeinen Einrichtungen des Objekts:',
    pricing: {
      klassePreise: { klein: 180, mittel: 240, gross: 290 },
    },
  },
  innenreinigung: {
    id: 'innenreinigung',
    label: 'Innenreinigung',
    reihenfolge: 2,
    defaultBausteinBezeichnung: 'Innenreinigung Treppenhaus & Flur',
    defaultKalkulationstyp: 'pauschal',
    defaultFrequenz: { count: 1, period: 'weekly' },
    defaultBeschreibungIntro:
      'Innenreinigung der Treppenhäuser und gemeinschaftlichen Flure mit folgenden Leistungen:',
    pricing: {
      klassePreise: { klein: 30, mittel: 37.5, gross: 45 },
      frequenzFaktor: {
        '0.5': 0.5,
        '1': 1.0,
        '2': 1.9,
        '3': 2.7,
        '4': 3.4,
        '5': 4.0,
        '6': 4.5,
        '7': 5.0,
      },
    },
  },
  aussenreinigung: {
    id: 'aussenreinigung',
    label: 'Außenreinigung',
    reihenfolge: 3,
    defaultBausteinBezeichnung: 'Außenreinigung Gehwege & Höfe',
    defaultKalkulationstyp: 'pauschal',
    defaultFrequenz: { count: 1, period: 'weekly' },
    defaultBeschreibungIntro:
      'Reinigung der befestigten Außenflächen und der öffentlichen Gehwege im Bereich des Objekts:',
    pricing: { euroProQm: 0.45 },
  },
  gruenflaechenpflege: {
    id: 'gruenflaechenpflege',
    label: 'Grünflächenpflege',
    reihenfolge: 4,
    saison: '01.04.–31.10.',
    defaultBausteinBezeichnung: 'Grünflächenpflege (Saison)',
    defaultKalkulationstyp: 'saisonal_verteilt',
    defaultFrequenz: { count: 2, period: 'monthly' },
    defaultBeschreibungIntro:
      'Pflege der Grünflächen während der Saison vom 01.04. bis 31.10. mit folgenden Leistungen:',
    pricing: { euroProQm: 0.9 },
  },
  winterdienst: {
    id: 'winterdienst',
    label: 'Winterdienst',
    reihenfolge: 5,
    saison: '01.11.–31.03.',
    defaultBausteinBezeichnung: 'Winterdienst (Bereitschaft + Einsatz)',
    defaultKalkulationstyp: 'bereitschaft_plus_einsatz',
    defaultFrequenz: { count: 1, period: 'agreed' },
    defaultBeschreibungIntro:
      'Räum- und Streudienst auf allen relevanten Verkehrsflächen während der Saison vom 01.11. bis 31.03.:',
    pricing: { euroProQm: 0.6 },
  },
  bereitschaftsdienst: {
    id: 'bereitschaftsdienst',
    label: 'Bereitschaftsdienst',
    reihenfolge: 6,
    defaultBausteinBezeichnung: 'Bereitschaftsdienst (Notruf 24/7)',
    defaultKalkulationstyp: 'pauschal',
    defaultFrequenz: { count: 1, period: 'ongoing' },
    defaultBeschreibungIntro:
      'Bereitschaft für Notfälle (Wasserschaden, Heizungsausfall, eingeschlossene Person etc.) inklusive einer monatlichen Pauschale.',
    pricing: { klein: 55, mittel: 75, gross: 95 },
  },
  sonderarbeiten: {
    id: 'sonderarbeiten',
    label: 'Sonderarbeiten',
    reihenfolge: 7,
    defaultBausteinBezeichnung: 'Sonderarbeiten (nach Aufwand)',
    defaultKalkulationstyp: 'pauschal',
    defaultBeschreibungIntro:
      'Sonderarbeiten werden nach Aufwand abgerechnet. Stundensatz und Anfahrt nach Vereinbarung.',
    pricing: {},
  },
};

export const PRICING_RUNDUNGSSCHRITT_DEFAULT = 5;
